import * as React from 'react'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { SideDrawerForm } from '@sales-center/components'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Box from '@mui/material/Box'
import { BranchDetails } from '../BranchDetails'
import { useSelector } from 'react-redux'
import { selectAllBranches } from '@user/store/branches/slice'
import { SalesTeam } from '@user/store/salesTeam/typings'
import { getStyles } from './useStyles'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { RadioWithLabel } from '../../Notifications/forms/ManageSignedAgreementSlideout/RadioWithLabel'
import RadioGroup from '@mui/material/RadioGroup'
import { Field, useFormikContext } from 'formik'
import { BaseTextField } from '@user/components/Formik'
import { useTheme } from '@mui/material/styles'

interface Props {
  onClose: () => void
  editTeam?: SalesTeam
}

export const AddSalesTeamForm: FC<Props> = (props) => {
  const { onClose, editTeam } = props
  const {
    dirty,
    isValid,
    isValidating,
    isSubmitting,
    errors,
    values,
    handleBlur,
    setFieldValue,
    setValues,
    submitForm,
  } = useFormikContext<SalesTeam>()
  const { branchId } = values

  // Hides company default sales team functionality
  const hideCompanyDefaultSalesTeam = true
  const branches = useSelector(selectAllBranches)
  const salesTeams = useSelector(selectAllSalesTeams)

  const disableSave = useMemo(() => {
    return !dirty || !isValid || isValidating || isSubmitting
  }, [dirty, isValid, isValidating, isSubmitting])

  const branch = useMemo(() => {
    return branches.find((x) => x.id == branchId)
  }, [branchId, branches])

  const onChangeBranch = useCallback(
    (event: React.ChangeEvent<{ value: string }>) => {
      const branchId = event.target.value
      const branchName = branches.find((x) => x.id == branchId)?.name
      if (branchName) {
        setValues((current) => ({
          ...current,
          branchId: branchId,
          branchName: branchName,
        }))
      }
    },
    [setValues, branches],
  )

  useEffect(() => {
    setFieldValue(
      'tenantDefault',
      hideCompanyDefaultSalesTeam
        ? false
        : !salesTeams.find((team) => team.tenantDefault == true),
      false,
    )
  }, [
    values.tenantDefault,
    salesTeams,
    hideCompanyDefaultSalesTeam,
    setFieldValue,
  ])
  const styles = getStyles(useTheme())
  return (
    <SideDrawerForm
      onSave={submitForm}
      onCancel={onClose}
      disableSave={disableSave}
    >
      <Box p={2}>
        <Box mb={2}>
          <Box component="span" sx={styles.formLabel}>
            Sales Team Name
          </Box>

          <Field
            name="name"
            as={TextField}
            disabled={isSubmitting}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            label="Name"
            margin="dense"
            onBlur={handleBlur}
            required
            type="text"
            variant="outlined"
          />
        </Box>

        <Box mb={2}>
          <Box component="span" sx={styles.formLabel}>
            Map to Branch
          </Box>
          <Field
            onChange={onChangeBranch}
            label="Branch"
            name="branchId"
            component={BaseTextField}
            select
            required
            size="small"
            variant="outlined"
            color="primary"
            margin="dense"
            disabled={!!editTeam}
          >
            {branches.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Field>
        </Box>

        <Box mb={2}>
          <Box component="span" sx={styles.formLabel}>
            Branch Details
          </Box>

          <BranchDetails branch={branch} />
        </Box>
        <Box mb={2}>
          <Box component="span" sx={styles.formLabel}>
            Record Sharing
          </Box>

          <RadioGroup
            value={values.structuredSharing}
            onChange={(_, value) => setFieldValue('structuredSharing', value)}
            name="structuredSharing"
          >
            <RadioWithLabel
              label="Collaborative Sharing"
              helperText="Allow users to access each other’s records across an entire sales team, regardless of position.  Users will still be limited to the permissions in their role"
              value="false"
            />

            <RadioWithLabel
              label="Structured Sharing"
              helperText="Follows hierarchical sharing rules. Users can only access records assigned to them or any position below them. Users will still be limited to the permissions in their role"
              value="true"
            />
          </RadioGroup>
        </Box>
      </Box>
    </SideDrawerForm>
  )
}
